import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
  ArrowRight, ArrowLeft, ArrowsClockwise, Printer, Sparkle,
} from "@phosphor-icons/react";
import SEOHead from "@/lib/seo/SEOHead";

/* --------- Design tokens (McKinsey-grade consulting palette) --------- */
const INK        = "#0B0F14";
const PAPER      = "#FBFAF7";   // Warm off-white, premium-paper feel
const MUTED      = "#5B6470";
const HAIRLINE   = "#E3E1DC";
const ACCENT     = "#2D5BFF";   // RankTrust blue — used sparingly
const GOOD       = "#0F766E";
const WARN       = "#B45309";
const BAD        = "#B91C1C";

/* Currency formatter tuned for executive-report presentation. */
function fmtCurrency(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (v >= 10_000)    return `$${(v / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${v.toLocaleString()}`;
}

/* --------- Small primitives --------- */
function SectionHeader({ number, title, subtitle }) {
  return (
    <header className="mb-8">
      <div className="text-[11px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>
        Section {number}
      </div>
      <h2 className="font-black tracking-tight text-3xl md:text-4xl mt-1" style={{ color: INK }}>{title}</h2>
      {subtitle && (
        <p className="mt-3 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: MUTED }}>{subtitle}</p>
      )}
    </header>
  );
}

function Divider() {
  return <div className="my-16 h-px" style={{ background: HAIRLINE }} />;
}

function StatBlock({ label, value, unit, hint }) {
  return (
    <div>
      <div className="text-[11px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>{label}</div>
      <div className="font-black text-3xl md:text-4xl mt-2 leading-none tabular-nums break-words" style={{ color: INK }}>
        {value}
        {unit && <span className="ml-1 text-lg font-bold" style={{ color: MUTED }}>{unit}</span>}
      </div>
      {hint && <div className="mt-2 text-sm" style={{ color: MUTED }}>{hint}</div>}
    </div>
  );
}

function ScoreBar({ label, score, interpreter }) {
  const color = score >= 70 ? GOOD : score >= 40 ? WARN : BAD;
  return (
    <div className="py-5" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
      <div className="flex items-baseline justify-between gap-4">
        <div className="font-bold text-lg" style={{ color: INK }}>{label}</div>
        <div className="font-mono text-2xl font-black tabular-nums" style={{ color }}>{score}<span className="text-sm font-normal" style={{ color: MUTED }}>/100</span></div>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full" style={{ background: HAIRLINE }}>
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>{interpreter}</p>
    </div>
  );
}

function PriorityPill({ priority }) {
  const c = priority === "Critical" ? BAD : priority === "High" ? WARN : MUTED;
  return (
    <span
      className="inline-block px-2 py-1 text-[10px] font-mono tracking-widest uppercase rounded"
      style={{ background: `${c}14`, color: c, border: `1px solid ${c}33` }}
    >
      {priority}
    </span>
  );
}

function BulletList({ items, color = INK }) {
  if (!items || !items.length) return null;
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
          <span className="text-base leading-relaxed" style={{ color: INK }}>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* --------- Cover --------- */
function Cover({ bp }) {
  return (
    <section className="pt-4 md:pt-8">
      <div className="text-[11px] font-mono tracking-[0.3em] uppercase" style={{ color: ACCENT }}>
        Executive Growth Blueprint
      </div>
      <h1 className="font-black tracking-tight text-5xl md:text-7xl mt-4 leading-[0.95]" style={{ color: INK }}>
        {bp.business_name}
      </h1>
      <p className="mt-3 text-lg md:text-xl" style={{ color: MUTED }}>
        {bp.keyword} · {bp.city}, {bp.state}
      </p>
      <p className="mt-6 text-sm md:text-base italic max-w-2xl" style={{ color: MUTED }}>
        AI-Powered Executive Business Intelligence &amp; Growth Strategy
      </p>
      <p className="mt-8 text-sm font-mono" style={{ color: MUTED }}>
        Prepared by RankTrust · {new Date(bp.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} · Version {bp.version}
      </p>
    </section>
  );
}

/* --------- 01. AI Business Growth Assessment --------- */
function AssessmentSection({ bp }) {
  const es = bp.sections.executive_summary || {};
  const revenue = bp.revenue_opportunity || {};
  const s = bp.scorecards || {};
  return (
    <section data-testid="section-assessment">
      <SectionHeader number="01" title="AI Business Growth Assessment" subtitle="The executive diagnosis — where the business stands today across the growth-critical dimensions." />

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10 pb-10" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
        <StatBlock label="Overall Growth Score" value={bp.overall_growth_score} unit="/100" />
        <StatBlock label="Est. Monthly Revenue Opportunity" value={fmtCurrency(revenue.estimated_monthly_revenue_opportunity)} />
        <StatBlock label="Est. Annual Revenue Opportunity" value={fmtCurrency(revenue.estimated_annual_revenue_opportunity)} />
        <StatBlock label="Est. Additional Monthly Customers" value={revenue.estimated_additional_customers ?? 0} />
      </div>

      {/* Scorecards */}
      <div className="mb-12">
        <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: MUTED }}>Business Growth Scorecards</div>
        <ScoreBar label="Visibility"          score={s.visibility?.score ?? 0}          interpreter={s.visibility?.interpreter} />
        <ScoreBar label="Trust"               score={s.trust?.score ?? 0}               interpreter={s.trust?.interpreter} />
        <ScoreBar label="Website Health"      score={s.website?.score ?? 0}             interpreter={s.website?.interpreter} />
        <ScoreBar label="AI Visibility"       score={s.ai_visibility?.score ?? 0}       interpreter={s.ai_visibility?.interpreter} />
        <ScoreBar label="Business Readiness"  score={s.business_readiness?.score ?? 0}  interpreter={s.business_readiness?.interpreter} />
      </div>

      {/* Headlines */}
      {es.biggest_opportunity_headline && (
        <div className="mb-6" data-testid="biggest-opportunity">
          <div className="text-[11px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Biggest Opportunity</div>
          <p className="text-xl md:text-2xl font-bold mt-2 leading-snug" style={{ color: INK }}>{es.biggest_opportunity_headline}</p>
        </div>
      )}
      {es.biggest_constraint_headline && (
        <div className="mb-10" data-testid="biggest-constraint">
          <div className="text-[11px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Biggest Growth Constraint</div>
          <p className="text-xl md:text-2xl font-bold mt-2 leading-snug" style={{ color: INK }}>{es.biggest_constraint_headline}</p>
        </div>
      )}

      {/* Executive Summary narrative */}
      <div>
        <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: MUTED }}>Executive Summary</div>
        <div className="prose prose-lg max-w-none">
          {(es.narrative_paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className="text-base md:text-lg leading-relaxed mb-5" style={{ color: INK }}>{p}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------- 02. Executive Business Intelligence --------- */
function BusinessIntelligence({ bp }) {
  const bi = bp.sections.executive_business_intelligence || {};
  const opps = bp.sections.top_five_opportunities || [];
  if (!bi.observation_paragraphs?.length && !opps.length) return null;
  return (
    <section data-testid="section-business-intelligence">
      <SectionHeader number="02" title="Executive Business Intelligence" subtitle="Strategic interpretation — what the data reveals when read as a business advisor rather than an analyst." />

      {/* Observation narrative */}
      {(bi.observation_paragraphs || []).length > 0 && (
        <div className="prose prose-lg max-w-none mb-10">
          {(bi.observation_paragraphs || []).filter(Boolean).map((p, i) => (
            <p key={i} className="text-base md:text-lg leading-relaxed mb-5" style={{ color: INK }}>{p}</p>
          ))}
        </div>
      )}

      {/* Constraints + Opportunities columns */}
      {(bi.key_growth_constraints?.length || bi.biggest_opportunities?.length) && (
        <div className="grid md:grid-cols-2 gap-10 mb-12">
          {bi.key_growth_constraints?.length > 0 && (
            <div>
              <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: BAD }}>Key Growth Constraints</div>
              <BulletList items={bi.key_growth_constraints} color={BAD} />
            </div>
          )}
          {bi.biggest_opportunities?.length > 0 && (
            <div>
              <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: GOOD }}>Biggest Opportunities</div>
              <BulletList items={bi.biggest_opportunities} color={GOOD} />
            </div>
          )}
        </div>
      )}

      {/* Top Five detailed opportunities */}
      {opps.length > 0 && (
        <div>
          <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-5" style={{ color: MUTED }}>Top Five Growth Opportunities</div>
          <div className="space-y-8">
            {opps.slice(0, 5).map((o, i) => (
              <article key={i} className="pb-8" style={{ borderBottom: i < opps.length - 1 ? `1px solid ${HAIRLINE}` : "none" }}>
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <div className="font-mono text-sm" style={{ color: MUTED }}>0{i + 1}</div>
                  <div className="flex gap-2">
                    <PriorityPill priority={o.priority || "High"} />
                    <span className="inline-block px-2 py-1 text-[10px] font-mono tracking-widest uppercase rounded"
                          style={{ background: `${MUTED}11`, color: MUTED, border: `1px solid ${HAIRLINE}` }}>
                      {o.estimated_difficulty || "Moderate"} difficulty
                    </span>
                  </div>
                </div>
                <h3 className="font-black text-2xl md:text-3xl leading-tight" style={{ color: INK }}>{o.title}</h3>
                <div className="grid md:grid-cols-3 gap-6 mt-5">
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Current Situation</div>
                    <p className="mt-1.5 text-sm md:text-base leading-relaxed" style={{ color: INK }}>{o.current_situation}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Business Impact</div>
                    <p className="mt-1.5 text-sm md:text-base leading-relaxed" style={{ color: INK }}>{o.business_impact}</p>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Recommended Next Step</div>
                    <p className="mt-1.5 text-sm md:text-base leading-relaxed" style={{ color: INK }}>{o.recommended_next_step}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/* --------- 03. Competitor Analysis --------- */
function CompetitorAnalysis({ bp }) {
  const ca = bp.sections.competitor_analysis || {};
  const anyContent = ca.current_position_narrative || (ca.competitive_strengths || []).length || (ca.competitive_weaknesses || []).length;
  if (!anyContent) return null;
  return (
    <section data-testid="section-competitor-analysis">
      <SectionHeader number="03" title="Competitor Analysis" subtitle="Where this business sits in the competitive landscape — read in business terms, not audit metrics." />

      {ca.current_position_narrative && (
        <p className="text-base md:text-lg leading-relaxed mb-10" style={{ color: INK }}>{ca.current_position_narrative}</p>
      )}

      <div className="grid md:grid-cols-2 gap-10 mb-10">
        {ca.competitive_strengths?.length > 0 && (
          <div>
            <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: GOOD }}>Competitive Strengths</div>
            <BulletList items={ca.competitive_strengths} color={GOOD} />
          </div>
        )}
        {ca.competitive_weaknesses?.length > 0 && (
          <div>
            <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: BAD }}>Competitive Weaknesses</div>
            <BulletList items={ca.competitive_weaknesses} color={BAD} />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        {ca.competitive_opportunities?.length > 0 && (
          <div>
            <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: ACCENT }}>Competitive Opportunities</div>
            <BulletList items={ca.competitive_opportunities} color={ACCENT} />
          </div>
        )}
        {ca.areas_where_competitors_lead?.length > 0 && (
          <div>
            <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: WARN }}>Where Competitors Lead</div>
            <BulletList items={ca.areas_where_competitors_lead} color={WARN} />
          </div>
        )}
      </div>
    </section>
  );
}

/* --------- 04. Revenue Opportunity Analysis --------- */
function RevenueAnalysis({ bp }) {
  const r = bp.revenue_opportunity || {};
  const commentary = bp.sections.revenue_analysis_commentary || "";
  return (
    <section data-testid="section-revenue-analysis">
      <SectionHeader number="04" title="Revenue Opportunity Analysis" subtitle="A conservative, data-driven estimate of what the identified opportunity is worth." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10 pb-10" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
        <StatBlock label="Est. Monthly Leads Today" value={r.estimated_monthly_leads_now ?? 0} />
        <StatBlock label="Est. Monthly Leads Potential" value={r.estimated_monthly_leads_potential ?? 0} />
        <StatBlock label="Est. Additional Customers" value={r.estimated_additional_customers ?? 0} hint="Monthly, if opportunity is captured" />
        <StatBlock label="Est. Annual Revenue Opportunity" value={fmtCurrency(r.estimated_annual_revenue_opportunity)} />
      </div>

      {commentary && <p className="text-base md:text-lg leading-relaxed mb-8" style={{ color: INK }}>{commentary}</p>}

      <div className="pt-6" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
        <div className="text-[11px] font-mono tracking-[0.2em] uppercase mb-3" style={{ color: MUTED }}>Assumptions</div>
        <ul className="space-y-2 list-disc pl-5">
          {(r.assumptions || []).map((a, i) => (
            <li key={i} className="text-sm leading-relaxed" style={{ color: MUTED }}>{a}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* --------- 05. Prioritized Growth Roadmap --------- */
function Roadmap({ bp }) {
  const r = bp.sections.roadmap_90_day || {};
  const phases = [
    { key: "immediate_priorities", label: "Immediate Priorities", accent: BAD },
    { key: "day_30", label: "First 30 Days", accent: ACCENT },
    { key: "day_60", label: "Days 31–60",    accent: ACCENT },
    { key: "day_90", label: "Days 61–90",    accent: ACCENT },
  ];
  return (
    <section data-testid="section-roadmap">
      <SectionHeader number="05" title="Prioritized Growth Roadmap" subtitle="The highest-impact initiatives sequenced by urgency. Business outcomes first, tactics deferred to onboarding." />
      <div className="grid md:grid-cols-2 gap-6">
        {phases.map(({ key, label, accent }) => {
          const p = r[key] || {};
          if (!p.primary_objective) return null;
          return (
            <div key={key} className="p-6" style={{ background: "#F5F3EE", border: `1px solid ${HAIRLINE}`, borderRadius: 12 }}>
              <div className="text-[11px] font-mono tracking-[0.25em] uppercase" style={{ color: accent }}>{label}</div>
              <div className="mt-4">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Primary Objective</div>
                <p className="mt-1 text-base font-bold leading-snug" style={{ color: INK }}>{p.primary_objective}</p>
              </div>
              <div className="mt-4">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Expected Business Outcome</div>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: INK }}>{p.expected_business_outcome}</p>
              </div>
              <div className="mt-4">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Why It Matters</div>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: MUTED }}>{p.why_it_matters || p.recommended_focus}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* --------- 06. Executive Growth Blueprint (Strategic Initiatives) --------- */
function StrategicInitiatives({ bp }) {
  const si = bp.sections.strategic_initiatives || bp.sections.executive_recommendation || {};
  const narrative = si.narrative || "";
  const initiatives = si.primary_initiatives || [];
  if (!narrative && !initiatives.length) return null;
  return (
    <section data-testid="section-strategic-initiatives">
      <SectionHeader number="06" title="Executive Growth Blueprint" subtitle="The strategic initiatives that will drive growth — the executive decisions to make." />
      {narrative && (
        <div className="prose prose-lg max-w-none mb-10">
          {narrative.split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-base md:text-lg leading-relaxed mb-5" style={{ color: INK }}>{p}</p>
          ))}
        </div>
      )}
      {initiatives.length > 0 && (
        <div>
          <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: ACCENT }}>Primary Strategic Initiatives</div>
          <BulletList items={initiatives} color={ACCENT} />
        </div>
      )}
    </section>
  );
}

/* --------- 07. RankTrustOS Recommendations --------- */
function RankTrustOSRecommendations({ bp }) {
  const items = bp.sections.growthos_recommendations || bp.sections.ranktrustos_recommendations || [];
  if (!items.length) return null;
  return (
    <section data-testid="section-ranktrustos">
      <SectionHeader number="07" title="RankTrustOS Recommendations" subtitle="The modules that address this specific business's diagnosed gaps." />
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((r, i) => (
          <div key={i} className="p-6" style={{ background: "#F5F3EE", border: `1px solid ${HAIRLINE}`, borderRadius: 12 }} data-testid={`ranktrustos-module-${i}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                   style={{ background: `${ACCENT}1F`, color: ACCENT }}>
                <Sparkle size={16} weight="fill" />
              </div>
              <div className="font-black text-lg" style={{ color: INK }}>{r.module}</div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Why Recommended</div>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: INK }}>{r.why_recommended}</p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase" style={{ color: MUTED }}>Expected Outcome</div>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: MUTED }}>{r.expected_business_outcome}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --------- 08. Financing Options --------- */
function FinancingOptions({ bp }) {
  const p = bp.sections.financing_options_positioning || "";
  if (!p) return null;
  return (
    <section data-testid="section-financing">
      <SectionHeader number="08" title="Financing Options" subtitle="How to fund the growth initiatives when cash flow is the constraint — not the ambition." />
      <p className="text-base md:text-lg leading-relaxed" style={{ color: INK }}>{p}</p>
      <p className="mt-6 text-xs italic" style={{ color: MUTED }}>
        Positioning only. No specific offers, lenders, rates, or dollar amounts implied. Any financing conversation would occur during onboarding.
      </p>
    </section>
  );
}

/* --------- 09. AI Sales Playbook --------- */
function SalesPlaybook({ bp }) {
  const p = bp.sections.ai_sales_playbook || {};
  const objections = p.likely_objections || [];
  if (!p.conversation_opener && !p.biggest_business_pain && objections.length === 0) return null;
  return (
    <section data-testid="section-sales-playbook">
      <SectionHeader
        number="09"
        title="AI Sales Playbook"
        subtitle="A customized playbook for anyone presenting this opportunity to the business owner."
      />

      {p.conversation_opener && (
        <div className="mb-8 p-6" style={{ background: "#F5F3EE", border: `1px solid ${HAIRLINE}`, borderRadius: 12 }}>
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>Suggested Conversation Opener</div>
          <p className="mt-3 text-lg md:text-xl leading-snug font-bold italic" style={{ color: INK }} data-testid="playbook-opener">
            &ldquo;{p.conversation_opener}&rdquo;
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8 mb-10">
        {p.biggest_business_pain && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>Biggest Business Pain</div>
            <p className="mt-2 text-base leading-relaxed" style={{ color: INK }}>{p.biggest_business_pain}</p>
          </div>
        )}
        {p.revenue_frame && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>Revenue Frame</div>
            <p className="mt-2 text-base leading-relaxed" style={{ color: INK }}>{p.revenue_frame}</p>
          </div>
        )}
      </div>

      {objections.length > 0 && (
        <div className="mb-10">
          <div className="text-[11px] font-mono tracking-[0.25em] uppercase mb-4" style={{ color: MUTED }}>Likely Objections &amp; Responses</div>
          <div className="space-y-5">
            {objections.map((o, i) => (
              <div key={i} className="pl-4" style={{ borderLeft: `2px solid ${ACCENT}` }} data-testid={`playbook-objection-${i}`}>
                <p className="text-base font-bold leading-snug" style={{ color: INK }}>&ldquo;{o.objection}&rdquo;</p>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>{o.suggested_response}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {p.executive_closing_strategy && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>Executive Closing Strategy</div>
            <p className="mt-2 text-base leading-relaxed" style={{ color: INK }}>{p.executive_closing_strategy}</p>
          </div>
        )}
        {p.next_meeting_objective && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase" style={{ color: MUTED }}>Next Meeting Objective</div>
            <p className="mt-2 text-base leading-relaxed" style={{ color: INK }}>{p.next_meeting_objective}</p>
          </div>
        )}
      </div>
    </section>
  );
}

/* --------- 10. CEO Action Sheet --------- */
function CEOActionSheet({ bp }) {
  const bullets = (bp.sections.ceo_action_sheet || {}).bullets || [];
  if (!bullets.length) return null;
  return (
    <section
      data-testid="section-ceo-action-sheet"
      className="py-16 md:py-20"
      style={{ pageBreakBefore: "always", background: INK, color: "white", borderRadius: 24, padding: "56px 32px" }}
    >
      <div className="text-[11px] font-mono tracking-[0.3em] uppercase" style={{ color: "#F59E0B" }}>
        Section 10 · The CEO Action Sheet
      </div>
      <h2 className="font-black tracking-tight text-4xl md:text-5xl mt-3 leading-[1.05]">
        If I Owned This Business,<br />Here&rsquo;s What I&rsquo;d Do Next.
      </h2>
      <p className="mt-4 text-sm md:text-base" style={{ color: "#94A3B8" }}>
        Five moves. No fluff. This is the page to bookmark.
      </p>
      <ol className="mt-12 space-y-8">
        {bullets.slice(0, 5).map((b, i) => (
          <li key={i} className="flex items-start gap-6" data-testid={`ceo-bullet-${i}`}>
            <div className="shrink-0 font-mono font-black text-2xl md:text-3xl tabular-nums"
                 style={{ color: "#F59E0B", minWidth: 44 }}>0{i + 1}</div>
            <p className="text-lg md:text-xl leading-[1.55] font-medium" style={{ color: "white" }}>{b}</p>
          </li>
        ))}
      </ol>
      <div className="mt-16 pt-8" style={{ borderTop: "1px solid #1F2937" }}>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-sm"
          style={{ background: ACCENT, color: "white" }}
          data-testid="ranktrustos-cta"
        >
          Continue Into RankTrustOS <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </section>
  );
}

/* --------- Main page --------- */
export default function Blueprint() {
  const { blueprintId } = useParams();
  const navigate = useNavigate();
  const [bp, setBp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const fetchOnce = async () => {
      try {
        const { data } = await axios.get(`${API}/public/blueprints/${blueprintId}`);
        if (cancelled) return;
        setBp(data);
        setLoading(false);
        if (data?.status === "generating") {
          timer = setTimeout(fetchOnce, 3000);
        } else if (data?.status === "failed") {
          toast.error(data.error ? `Generation failed: ${data.error}` : "Blueprint generation failed");
        }
      } catch {
        if (!cancelled) { toast.error("Blueprint not found"); setLoading(false); }
      }
    };
    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [blueprintId]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/public/blueprints/${blueprintId}/regenerate`);
      navigate(`/blueprint/${data.id}`);
      toast.success("Regenerating blueprint…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    }
    setRegenerating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PAPER }}>
        <div className="text-sm font-mono" style={{ color: MUTED }}>Loading blueprint…</div>
      </div>
    );
  }
  if (!bp) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PAPER }}>
        <div className="text-center">
          <div className="font-black text-2xl mb-4" style={{ color: INK }}>Blueprint not found</div>
          <Link to="/check" className="text-sm underline" style={{ color: ACCENT }}>Run a new assessment</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: PAPER, color: INK, minHeight: "100vh" }} data-testid="blueprint-page">
      <SEOHead
        title={bp?.business_name ? `Executive Growth Blueprint — ${bp.business_name} | RankTrust` : "Executive Growth Blueprint | RankTrust"}
        description="Executive Growth Blueprint — a customer-specific McKinsey-grade growth strategy generated by RankTrust."
        robots="noindex,follow"
        indexable={false}
      />
      {/* Sticky utility bar */}
      <div className="sticky top-0 z-10 backdrop-blur-md" style={{ background: `${PAPER}E6`, borderBottom: `1px solid ${HAIRLINE}` }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
          <button
            onClick={() => (bp.check_id ? navigate(`/check?cid=${bp.check_id}`) : navigate("/check"))}
            className="text-xs font-mono flex items-center gap-2 hover:underline"
            style={{ color: MUTED }}
            data-testid="back-to-assessment"
          >
            <ArrowLeft size={14} /> Back to Assessment
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={regenerate}
              disabled={regenerating || bp.status === "generating"}
              className="text-xs font-mono flex items-center gap-2 px-3 py-1.5 rounded border disabled:opacity-40"
              style={{ borderColor: HAIRLINE, color: INK }}
              data-testid="regenerate-blueprint"
            >
              <ArrowsClockwise size={14} className={regenerating ? "animate-spin" : ""} />
              {regenerating ? "Regenerating…" : "Regenerate"}
            </button>
            <button
              onClick={() => window.print()}
              disabled={bp.status === "generating"}
              className="text-xs font-mono flex items-center gap-2 px-3 py-1.5 rounded border disabled:opacity-40"
              style={{ borderColor: HAIRLINE, color: INK }}
              data-testid="print-blueprint"
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>
      </div>

      {/* Generating state */}
      {bp.status === "generating" && (
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-40 text-center" data-testid="blueprint-generating">
          <div className="text-[11px] font-mono tracking-[0.3em] uppercase" style={{ color: ACCENT }}>
            Executive Growth Blueprint
          </div>
          <h1 className="font-black tracking-tight text-4xl md:text-6xl mt-4 leading-[0.95]" style={{ color: INK }}>
            Preparing your Blueprint for<br />{bp.business_name}
          </h1>
          <p className="mt-6 text-base md:text-lg max-w-xl mx-auto leading-relaxed" style={{ color: MUTED }}>
            Our AI is synthesizing the executive assessment, business intelligence, competitor analysis, 90-day roadmap, RankTrustOS recommendations, sales playbook, and CEO Action Sheet from your assessment.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: ACCENT, animationDelay: "0ms" }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: ACCENT, animationDelay: "150ms" }} />
            <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: ACCENT, animationDelay: "300ms" }} />
          </div>
          <p className="mt-6 text-xs font-mono" style={{ color: MUTED }}>
            Typically takes 60–90 seconds. This page will update automatically.
          </p>
        </div>
      )}

      {/* Document body */}
      {bp.status !== "generating" && (
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Cover bp={bp} />
        <Divider />
        <AssessmentSection bp={bp} />
        <Divider />
        <BusinessIntelligence bp={bp} />
        <Divider />
        <CompetitorAnalysis bp={bp} />
        <Divider />
        <RevenueAnalysis bp={bp} />
        <Divider />
        <Roadmap bp={bp} />
        <Divider />
        <StrategicInitiatives bp={bp} />
        <Divider />
        <RankTrustOSRecommendations bp={bp} />
        <Divider />
        <FinancingOptions bp={bp} />
        <Divider />
        <SalesPlaybook bp={bp} />
        <Divider />
        <CEOActionSheet bp={bp} />

        <footer className="mt-24 pt-8 text-center text-xs font-mono" style={{ color: MUTED, borderTop: `1px solid ${HAIRLINE}` }}>
          <div className="flex items-center justify-center gap-2">
            <Sparkle size={12} weight="fill" style={{ color: ACCENT }} />
            <span>Powered by RankTrust</span>
          </div>
          <div className="mt-2">
            Version {bp.version} · Generated {new Date(bp.created_at).toLocaleDateString()} · This Executive Growth Blueprint is derived exclusively from the AI Business Growth Assessment for {bp.business_name}.
          </div>
        </footer>
      </div>
      )}
    </div>
  );
}
